import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Radio, Globe, MapPin, CalendarOff } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { Overline } from "../components/motion";
import { WorkCard } from "../components/tvui";
import { useTranslate } from "../context/TranslateContext";
import { getChannel, seriesForChannel } from "../mock/tv";

export default function ChannelProfile() {
  const { slug } = useParams();
  const { t } = useTranslate();
  const c = getChannel(slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!c) return (<div><InnerNav /><div className="p-20 text-center font-body text-ash">{t("Channel not found.")}</div></div>);
  const shows = seriesForChannel(slug);
  const listenText = `${c.name}, a ${c.languages.join(", ")} television channel from ${c.network}.`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-paper to-surface" style={{ background: `linear-gradient(180deg, ${c.accent}22 0%, #FBF6EC 62%)` }}>
      <InnerNav />
      <div className="mx-auto max-w-[1400px] px-6 py-14 md:px-12 md:py-16">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex h-28 w-28 shrink-0 items-center justify-center rounded-3xl border" style={{ background: c.accent + "22", borderColor: c.accent + "66" }}>
            <span className="font-display text-4xl font-semibold" style={{ color: c.accent }}>{c.name.split(" ").map((w) => w[0]).join("")}</span>
          </div>
          <div>
            <Overline style={{ color: c.accent }}>{t("Television Channel")}</Overline>
            <h1 data-testid="channel-name" className="mt-2 font-display text-5xl font-semibold tracking-tight text-cream md:text-6xl">{c.name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-4 font-body text-sm text-ash">
              <span className="flex items-center gap-1.5"><Radio className="h-4 w-4" />{c.network}</span>
              <span className="flex items-center gap-1.5"><Globe className="h-4 w-4" />{c.languages.join(", ")}</span>
              <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{c.area}</span>
            </div>
          </div>
          <div className="ml-auto"></div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {c.categories.map((x) => <span key={x} className="rounded-full border border-edge px-4 py-1.5 font-body text-sm text-ash">{t(x)}</span>)}
        </div>

        {/* Schedule — honest empty state */}
        <div className="mt-10 flex items-center gap-3 rounded-2xl border border-edge bg-surface/40 p-6" data-testid="channel-schedule">
          <CalendarOff className="h-6 w-6 text-saffron" />
          <div>
            <div className="font-body text-cream">{t("Schedule unavailable")}</div>
            <div className="font-body text-sm text-ash">{t("No licensed programme-guide feed is connected for this channel.")}</div>
          </div>
        </div>

        <h2 className="mt-14 mb-6 font-display text-3xl font-semibold text-cream">{t("Programmes on")} {c.name}</h2>
        {shows.length > 0 ? (
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">{shows.map((s, i) => <WorkCard key={s.slug} s={s} i={i} />)}</div>
        ) : <p className="font-body text-ash" data-testid="channel-no-shows">{t("No programmes catalogued yet.")}</p>}
      </div>
    </div>
  );
}
